import {
    CONTRACT_EXTENSION,
    CONTRACT_MAX_SIZE_MB,
    CONTRACT_MIME_TYPE,
    CONTRACT_STATUS,
    CONTRACT_STATUS_LABEL,
    DOCUMENT_ERROR,
    OBJECT_URL_TTL_MS,
    PREVIEW_KIND,
    PUBLISHER_CONTRACT_STATUS,
    PUBLISHER_CONTRACT_STATUS_LABEL,
    PUBLISHER_VIEW_LABEL
} from "./constants";
import { formatFileSize } from "@helpers/utils";

const BYTES_IN_MB = 1024 * 1024;

// Re-exported so the contract screens keep importing their file helpers from
// one place; the implementation is shared with the file uploader.
export { formatFileSize };

/**
 * Guards the picker and the drop target against unsupported files before any
 * network call. Returns an error message, or null when the file is accepted.
 * @param file
 */
export const validateContractFile = (file) => {
    if (!file) return "No file selected.";

    const name = file.name?.toLowerCase() || "";
    const isPdf = file.type === CONTRACT_MIME_TYPE || name.endsWith(CONTRACT_EXTENSION);

    if (!isPdf) return "Unsupported file type. The contract must be a PDF.";
    if (file.size > CONTRACT_MAX_SIZE_MB * BYTES_IN_MB) return `File is too large. The limit is ${CONTRACT_MAX_SIZE_MB} MB.`;

    return null;
};

/**
 * The renderer for a document the /document route served, off its content
 * type. The route serves PDFs; an empty type is read as one too, since that is
 * what the route promises.
 * @param type the blob's MIME type
 */
export const previewKindOfType = (type = "") => (
    !type || type.includes("pdf") ? PREVIEW_KIND.PDF : PREVIEW_KIND.DOC
);

/**
 * The message behind a failed /document request. The route answers errors as
 * ordinary JSON, but the request asked for a blob, so the body has to be read
 * back as text before it can be parsed.
 * @param error
 */
export const documentErrorOf = async (error) => {
    const body = error?.response?.data;

    if (body instanceof Blob) {
        try {
            return JSON.parse(await body.text()).message || DOCUMENT_ERROR;
        } catch {
            return DOCUMENT_ERROR;
        }
    }

    return body?.message || DOCUMENT_ERROR;
};

/**
 * A 401/403 is already handled by useAxios with its session-expired dialog, so
 * the contract screens must not stack a toast on top of it.
 * @param error
 */
export const isSessionError = (error) => [401, 403].includes(error?.response?.status);

/**
 * The name a downloaded contract is saved under. Always ends in `.pdf`,
 * because that is what the /document route serves.
 * @param name
 */
export const pdfFileName = (name = "") => {
    const base = name && name !== "-" ? name.replace(/\.pdf$/i, "") : "contract";
    return `${base}.pdf`;
};

/**
 * Saves a blob URL to disk under `fileName`. An anchor with `download` is not
 * a popup, so it still works after the await that fetched the bytes.
 * @param url
 * @param fileName
 */
export const saveObjectUrl = (url, fileName) => {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_TTL_MS);
};

/**
 * An Account/HolderAccess row's `contract_status` as a known value. Anything
 * unrecognised reads as `not_sent`, which is what the list endpoints default to.
 * @param raw
 */
export const contractStatusOf = (raw = {}) => {
    const value = String(raw.contract_status ?? "").toLowerCase();
    return CONTRACT_STATUS_LABEL[value] ? value : CONTRACT_STATUS.NOT_SENT;
};

/**
 * A brand + publisher contract's `status` as a known value. A publisher with
 * no contract yet comes back as `not_sent`, and so does anything unrecognised.
 * @param status
 */
export const publisherContractStatusOf = (status) => (
    PUBLISHER_CONTRACT_STATUS_LABEL[status] ? status : PUBLISHER_CONTRACT_STATUS.NOT_SENT
);

/**
 * Whoever a populated user/holder reference resolves to. These arrive either
 * as a plain string or as a populated document depending on the endpoint;
 * always reduce to a string — an object here reaches the DOM as a React child
 * and throws.
 * @param value
 */
export const holderName = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return value.holder_name || value.name || value.email || "";
};

/**
 * One party's signature, or null while that party has not signed.
 * @param raw
 */
const signatureOf = (raw) => (raw ? {
    name    : raw.name || "",
    signedAt: raw.signed_at || null
} : null);

/**
 * The per-party signatures stamped onto the contract. Null for a publisher
 * who has not been sent one yet.
 * @param raw
 */
export const normalizeSignatures = (raw) => (raw ? {
    ops      : signatureOf(raw.ops),
    brand    : signatureOf(raw.brand),
    publisher: signatureOf(raw.publisher)
} : null);

/**
 * One row of a `/contract/publishers` listing. Ops and the brand portal get
 * exactly the same shape. A publisher with no contract yet has `_id: null`,
 * so the publisher's holder id is the stable row key.
 * @param raw
 */
export const normalizePublisherContract = (raw = {}) => {
    const publisher = raw.publisher || {};

    return {
        publisherId: publisher.holder_access_id || "",
        name       : publisher.holder_name || "-",
        email      : publisher.email || "",
        // What every `/contract/publishers/:contractId` route is addressed by.
        contractId : raw._id || null,
        version    : raw.version ?? null,
        status     : publisherContractStatusOf(raw.status),
        sentAt     : raw.sent_at || null,
        completedAt: raw.completed_at || null,
        signatures : normalizeSignatures(raw.signatures)
    };
};

/**
 * The label on a publisher row's View action.
 * @param row a normalizePublisherContract row
 */
export const publisherViewLabelOf = (row = {}) => (
    row.status === PUBLISHER_CONTRACT_STATUS.SIGNED ? PUBLISHER_VIEW_LABEL.signed : PUBLISHER_VIEW_LABEL.default
);

/**
 * The list's `counts` block. It always covers every publisher of the brand,
 * whatever page or filter the rows are on.
 * @param raw `data.counts`
 */
export const normalizeContractCounts = (raw = {}) => ({
    total            : raw.total ?? 0,
    notSent          : raw.not_sent ?? 0,
    queued           : raw.queued ?? 0,
    awaitingBrand    : raw.awaiting_brand ?? 0,
    brandSignQueued  : raw.brand_sign_queued ?? 0,
    awaitingPublisher: raw.awaiting_publisher ?? 0,
    signed           : raw.signed ?? 0
});

/**
 * The `contract_meta` the save endpoint expects, from an uploader response.
 * @param staged
 */
export const toContractMeta = (staged = {}) => ({
    name         : staged.name,
    original_name: staged.originalName,
    size         : staged.size
});
